'use client'

import { format } from 'date-fns/format'
import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

import { type SetType } from '@/defs/set'
import { type TagType } from '@/defs/tag'

export type MiscContextType = {
  articles: {
    author: string
    slug: string
    title: string
  }[]
  guides: {
    author: string
    slug: string
    title: string
  }[]
  precons: {
    name: string
    precons: {
      colors: string
      name: string
      slug: string
    }[]
    released_at: string
    spoiled_at: string
    tag: string
  }[]
  sets: SetType[]
  tags: TagType[]
  tcgplayerKickback?: {
    end?: Date
    percent?: number
    start?: Date
  }
  tcgplayerSale?: {
    end?: string
    percent?: number
  }
}

const defaultContext: MiscContextType = { articles: [], guides: [], precons: [], sets: [], tags: [] }

const MiscContext = createContext<MiscContextType>(defaultContext)

type Props = {
  children: ReactNode
  misc?: MiscContextType
}

export const MiscProvider = ({ children, misc }: Props) => {
  const value = useMemo(() => {
    if (!misc) return defaultContext
    const { end, percent, start } = misc.tcgplayerKickback || {}
    const now = new Date()
    const tcgplayerSale =
      !!start && !!end && !!percent && now > new Date(start) && now < new Date(end)
        ? {
            end: format(new Date(end), 'MMM do, yyyy h:mmaaa OOOO'),
            percent,
          }
        : undefined

    return {
      ...misc,
      tcgplayerSale,
    }
  }, [misc])

  return <MiscContext.Provider value={value}>{children}</MiscContext.Provider>
}

export const useMisc = (): MiscContextType => {
  const misc = useContext(MiscContext)

  return misc
}
